import DesktopDownload from './download/DesktopDownload';
import { getRemainingTransactions } from './game/remainingTransactions';
import useLastJackpotClaim from './useLastJackpotClaim';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SlotMachine from './game/SlotMachine.jsx';
import { buildSlotState } from './slotAdapter.js';
import { settleSpinResultLines } from './game/slotEngine.js';
import { playBrokenLever, playLeverFusePop, playBuyButtonSynth, playConnectPrompt, playError, playFooterButtonSynth, playGeckoLaunchSynth, playJackpotAward, playJackpotClaim, playLever, playNeonLogoFault, playPopupButtonSynth, playPopupOpenSynth, playSellButtonSynth, playSideButtonSynth, playTxCounterTick, playMultiplierSelect, playPrize, playSpinLoopHint, playTxConfirmedNeutral, unlockAudio } from './game/sound.js';
import logoUrl from './assets/startax-logo.png';
import geckoButtonUrl from './assets/coingecko-symbol.svg';
import { GECKO_BUTTON_UI, MULTIPLIER_RAIL_UI } from './multiplierRailUiConfig.js';
import { LEVER_SWAP_CONFIG } from '../swap/swapConfig.js';
import './slot.css';
import './vanguardScenePosition.css';
import './vanguardViewportFrameLock.css';
import './neonLogo.css';
import './activeRewardsWinners.css';
import './productionControls.css';
import './controlInteractions.css';

function format(value) {
  return new Intl.NumberFormat('es-ES').format(value || 0);
}

function shortAddress(address) {
  if (!address) return 'Connect';
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

function sameAddress(a, b) {
  return Boolean(a && b) && String(a).toLowerCase() === String(b).toLowerCase();
}

function isZeroAddress(address) {
  return !address || String(address).toLowerCase() === ZERO_ADDRESS;
}

function openOptimismTx(txHash) {
  if (!txHash) return;
  window.open(`https://optimistic.etherscan.io/tx/${txHash}`, '_blank', 'noopener,noreferrer');
}

function openOptimismAddress(address) {
  if (!address) return;
  window.open(`https://optimistic.etherscan.io/address/${address}#tokentxns`, '_blank', 'noopener,noreferrer');
}

function floorTokenAmount(value) {
  return Math.floor(Number(value || 0));
}

const SWAP_MULTIPLIER_OPTIONS = Object.freeze(LEVER_SWAP_CONFIG.SWAP_MULTIPLIERS || [1, 3, 5, 10, 100]);
const AUTO_LEVER_DELAY_MS = Math.max(600, Number(LEVER_SWAP_CONFIG.AUTO_LEVER_DELAY_MS || 1800));
const GECKO_TERMINAL_POOL_URL = 'https://www.geckoterminal.com/optimism/pools/0x2a0e0e256425bc5152ed19949c2ee740aea2f672?utm_source=coingecko&utm_medium=referral&utm_campaign=searchresults?utm_source=coingecko&utm_medium=referral&utm_campaign=searchresults';
const STARTAX_MANIFESTO_URL = 'https://forum.startax.win/t/the-startax-manifesto/60';
const SUSHI_SWAP_URL = 'https://www.sushi.com/optimism/swap?token0=NATIVE&token1=0xc756C791268D7C2728113A2Ff48cA46548200906';
const OPTIMISM_ADDRESS_BASE_URL = 'https://optimistic.etherscan.io/address/';
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

const ACTIVE_REWARDS_WINNER_LINES = Object.freeze([
  { key: 'StarTax10', label: 'StarTax10', milestone: 10, className: 'milestone-10' },
  { key: 'StarTax100', label: 'StarTax100', milestone: 100, className: 'milestone-100' },
  { key: 'StarTax1K', label: 'StarTax1K', milestone: 1000, className: 'milestone-1k' },
  { key: 'StarTax10K', label: 'StarTax10K', milestone: 10000, className: 'milestone-10k' },
  { key: 'StarTax100K', label: 'StarTax100K', milestone: 100000, className: 'milestone-100k' },
  { key: 'StarTax1M', label: 'StarTax1M', milestone: 1000000, className: 'milestone-1m' },
]);

const SLOT_NAV_LINKS = [
  {
    key: 'forum',
    label: 'FORUM',
    eyebrow: 'community',
    url: 'https://forum.startax.win/t/welcome-to-startax-forum/5',
  },
  {
    key: 'whitepaper',
    label: 'WHITEPAPER',
    eyebrow: 'docs',
    url: '/whitepaper',
  },
  {
    key: 'payouts',
    label: 'PAYOUTS',
    eyebrow: 'prize table',
    url: 'https://forum.startax.win/t/inmutable-prize-table/39',
  },
  {
    key: 'x',
    label: 'X',
    eyebrow: 'social',
    url: 'https://x.com/StarTaxDeFi',
  },
  {
    key: 'twitch',
    label: 'TWITCH',
    eyebrow: 'stream',
    url: 'https://www.twitch.tv/startaxdefi',
  },
];

function SlotNavIcon({ item }) {
  if (item.key === 'forum') {
    return <span className="slotNavForumMark" aria-hidden="true">FORUM</span>;
  }

  if (item.key === 'whitepaper') {
    return (
      <span className="slotNavIcon slotNavIconWhitepaper" aria-hidden="true">
        <svg viewBox="0 0 64 64" role="img" focusable="false">
          <path d="M18 6h21l11 11v41H18V6Z" />
          <path d="M39 7v12h12" />
          <path d="M25 30h20M25 39h20M25 48h14" />
        </svg>
      </span>
    );
  }

  if (item.key === 'payouts') {
    return (
      <span className="slotNavIcon slotNavIconPayouts" aria-hidden="true">
        <svg viewBox="0 0 64 64" role="img" focusable="false">
          <path d="M20 12h24v10c0 10-5 18-12 20-7-2-12-10-12-20V12Z" />
          <path d="M20 18H9c1 11 5 18 14 20M44 18h11c-1 11-5 18-14 20" />
          <path d="M32 42v8M24 54h16" />
        </svg>
      </span>
    );
  }

  if (item.key === 'x') {
    return <span className="slotNavIcon slotNavIconX" aria-hidden="true">𝕏</span>;
  }

  if (item.key === 'twitch') {
    return (
      <span className="slotNavIcon slotNavIconTwitch" aria-hidden="true">
        <svg viewBox="0 0 2400 2800" role="img" focusable="false">
          <path d="M500 0 0 500v1800h600v500h500l500-500h400l400-400V0H500zm1700 1800-300 300h-500l-500 500v-500H300V300h1900v1500z" />
          <path d="M1700 850h-250v700h250V850zm-650 0H800v700h250V850z" />
        </svg>
      </span>
    );
  }

  return null;
}

function openSlotNavLink(url) {
  if (!url) return;
  const targetUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  window.open(targetUrl, '_blank', 'noopener,noreferrer');
}

function openGeckoTerminalPool() {
  window.open(GECKO_TERMINAL_POOL_URL, '_blank', 'noopener,noreferrer');
}

function openStartaxManifesto() {
  window.open(STARTAX_MANIFESTO_URL, '_blank', 'noopener,noreferrer');
}

function openSushiSwap() {
  window.open(SUSHI_SWAP_URL, '_blank', 'noopener,noreferrer');
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const NEON_TAX_LETTERS = Object.freeze(['T', 'a', 'x']);
const NEON_LOGO_SUBTITLE_DEFAULT = 'The Original DeFi Slot Machine';
const NEON_LOGO_SUBTITLE_WINNERS = 'Active Rewards Winners';

function parseCssNumber(style, variableName, fallback) {
  const raw = style?.getPropertyValue(variableName)?.trim();
  if (!raw) return fallback;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : fallback;
}

function parseCssFlag(style, variableName, fallback = true) {
  const raw = style?.getPropertyValue(variableName)?.trim();
  if (!raw) return fallback;
  if (raw === '0' || raw.toLowerCase() === 'false' || raw.toLowerCase() === 'off') return false;
  return true;
}

function randomBetween(min, max) {
  const safeMin = Math.min(Number(min) || 0, Number(max) || 0);
  const safeMax = Math.max(Number(min) || 0, Number(max) || 0);
  return safeMin + Math.random() * (safeMax - safeMin);
}

function shuffleTaxLetterIndexes() {
  return [0, 1, 2]
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map((entry) => entry.value);
}

function pickNeonFaultCount(style) {
  const singleWeight = Math.max(0, parseCssNumber(style, '--brand-neon-fault-single-weight', 64));
  const pairWeight = Math.max(0, parseCssNumber(style, '--brand-neon-fault-pair-weight', 28));
  const tripleWeight = Math.max(0, parseCssNumber(style, '--brand-neon-fault-triple-weight', 8));
  const total = singleWeight + pairWeight + tripleWeight;
  if (total <= 0) return 1;
  const roll = Math.random() * total;
  if (roll < singleWeight) return 1;
  if (roll < singleWeight + pairWeight) return 2;
  return 3;
}

function buildNeonTaxFaultSequence(style) {
  const count = pickNeonFaultCount(style);
  const durationMin = parseCssNumber(style, '--brand-neon-fault-duration-min-ms', 220);
  const durationMax = parseCssNumber(style, '--brand-neon-fault-duration-max-ms', 560);
  const staggerMin = parseCssNumber(style, '--brand-neon-fault-stagger-min-ms', 18);
  const staggerMax = parseCssNumber(style, '--brand-neon-fault-stagger-max-ms', 115);
  const indexes = shuffleTaxLetterIndexes().slice(0, count);

  return indexes.reduce((acc, index, order) => {
    const delay = order === 0 ? 0 : Math.round(randomBetween(staggerMin, staggerMax) + order * 7);
    const duration = Math.round(randomBetween(durationMin, durationMax));
    acc[index] = { delay, duration };
    return acc;
  }, {});
}

export default function SlotMode({
  walletAddress,
  walletConnectNonce = 0,
  tokenBalance,
  confirmedReward = null,
  transactionCounter,
  transactionMinimum,
  currentReceivers,
  lastSwapAmount,
  lastSwapDebugReport,
  lastSuccessfulSwap,
  isCorrectNetwork = true,
  walletError = '',
  onConnectWallet,
  onDisconnectWallet,
  onLeverSwap,
  jackpotTotal = 0,
  jackpotFiboReceiver = '0x0000000000000000000000000000000000000000',
  jackpotFibPot = 0,
  jackpotLastFiboNumber = 0,
  onClaimJackpot,
}) {
  const [slotState, setSlotState] = useState(() =>
    buildSlotState({ transactionCounter, currentReceivers, walletAddress })
  );
  const [isPendingTx, setIsPendingTx] = useState(false);
  const [mode, setMode] = useState('BUY');
  const [swapMultiplier, setSwapMultiplier] = useState(LEVER_SWAP_CONFIG.DEFAULT_SWAP_MULTIPLIER || 1);
  const [status, setStatus] = useState('Ready');
  const [showWalletNotice, setShowWalletNotice] = useState(false);
  const [showNetworkNotice, setShowNetworkNotice] = useState(false);
  const [showDisconnectNotice, setShowDisconnectNotice] = useState(false);
  const [disconnectActionPending, setDisconnectActionPending] = useState(null);
  const [balanceNotice, setBalanceNotice] = useState(null);
  const [leverBroken, setLeverBroken] = useState(false);
  const [leverEnergy, setLeverEnergy] = useState({ power: 0, active: false, pulling: false, spinning: false, mode: 'BUY' });
  const [autoMode, setAutoMode] = useState(false);
  const [prizePulseActive, setPrizePulseActive] = useState(false);
  const [txPulseActive, setTxPulseActive] = useState(false);
  const [lastRewardAmount, setLastRewardAmount] = useState(0);
  const [jackpotCelebrating, setJackpotCelebrating] = useState(false);
  const [jackpotClaimPending, setJackpotClaimPending] = useState(false);
  const lastJackpot = useLastJackpotClaim(jackpotClaimPending);
  const [claimFailureNotice, setClaimFailureNotice] = useState(null);
  const [simulatedJackpot, setSimulatedJackpot] = useState(null);

  const latestDataRef = useRef({ transactionCounter, currentReceivers, walletAddress });
  const previousWalletConnectNonceRef = useRef(walletConnectNonce);
  const slotStateRef = useRef(slotState);
  const previousCounterRef = useRef(null);
  const prizePulseTimerRef = useRef(null);
  const txPulseTimerRef = useRef(null);
  const jackpotCelebrationTimerRef = useRef(null);
  const jackpotSimulationTimerRef = useRef(null);
  const lastCelebratedJackpotRef = useRef(null);
  const popupOpenStateRef = useRef(false);
  const autoModeRef = useRef(false);
  const autoTimerRef = useRef(null);
  const handleLeverRef = useRef(null);
  const neonLogoRef = useRef(null);
  const neonFaultTimerRef = useRef(null);
  const neonPhaseTimerRef = useRef(null);
  const [neonLogoState, setNeonLogoState] = useState({ phase: 'idle', sequence: {}, token: 0 });
  const [rewardsWinnersView, setRewardsWinnersView] = useState(false);

  const handleLogoRewardsToggle = useCallback(() => {
    unlockAudio();
    setRewardsWinnersView((previous) => !previous);
  }, []);

  const handleLogoRewardsToggleKeyDown = useCallback((event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleLogoRewardsToggle();
  }, [handleLogoRewardsToggle]);

  const activeRewardsWinnerRows = useMemo(() => (
    ACTIVE_REWARDS_WINNER_LINES.map((line) => {
      const address = currentReceivers?.[line.key] || ZERO_ADDRESS;
      const hasAddress = !isZeroAddress(address);
      const remaining = getRemainingTransactions(transactionCounter, line.milestone);
      const isConnectedWallet = Boolean(hasAddress && sameAddress(walletAddress, address));

      return {
        ...line,
        address,
        hasAddress,
        isConnectedWallet,
        remaining,
        explorerUrl: hasAddress ? `${OPTIMISM_ADDRESS_BASE_URL}${address}#tokentxns` : '',
      };
    })
  ), [currentReceivers, transactionCounter, walletAddress]);

  const resetLeverEnergyVisual = useCallback(() => {
    setLeverEnergy({ power: 0, active: false, pulling: false, spinning: false, mode });
  }, [mode]);

  const handleLeverEnergyChange = useCallback((payload = {}) => {
    const nextPower = Math.max(0, Math.min(1, Number(payload.power) || 0));
    setLeverEnergy((previous) => {
      const next = {
        power: nextPower,
        active: Boolean(payload.active),
        pulling: Boolean(payload.pulling),
        spinning: Boolean(payload.spinning),
        mode: payload.mode === 'SELL' ? 'SELL' : 'BUY',
      };
      if (
        Math.round(previous.power * 200) === Math.round(next.power * 200) &&
        previous.active === next.active &&
        previous.pulling === next.pulling &&
        previous.spinning === next.spinning &&
        previous.mode === next.mode
      ) {
        return previous;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let disposed = false;

    const clearNeonTimers = () => {
      if (neonFaultTimerRef.current) window.clearTimeout(neonFaultTimerRef.current);
      if (neonPhaseTimerRef.current) window.clearTimeout(neonPhaseTimerRef.current);
      neonFaultTimerRef.current = null;
      neonPhaseTimerRef.current = null;
    };

    const scheduleNextFault = () => {
      if (disposed) return;
      const node = neonLogoRef.current;
      if (!node || typeof window === 'undefined') return;
      const style = window.getComputedStyle(node);
      const minDelay = parseCssNumber(style, '--brand-neon-fault-min-delay-ms', 45000);
      const maxDelay = parseCssNumber(style, '--brand-neon-fault-max-delay-ms', 140000);
      const delay = Math.round(randomBetween(minDelay, maxDelay));

      neonFaultTimerRef.current = window.setTimeout(() => {
        if (disposed) return;
        const currentNode = neonLogoRef.current;
        if (!currentNode || typeof window === 'undefined') {
          scheduleNextFault();
          return;
        }

        const currentStyle = window.getComputedStyle(currentNode);
        const isNeonEnabled = parseCssFlag(currentStyle, '--brand-neon-enabled', true);
        const isFaultEnabled = parseCssFlag(currentStyle, '--brand-neon-fault-enabled', true);
        if (!isNeonEnabled || !isFaultEnabled) {
          setNeonLogoState((previous) => ({ ...previous, phase: 'idle', sequence: {} }));
          scheduleNextFault();
          return;
        }

        const riseMs = Math.max(0, parseCssNumber(currentStyle, '--brand-neon-voltage-rise-ms', 950));
        const holdMs = Math.max(0, parseCssNumber(currentStyle, '--brand-neon-voltage-hold-ms', 170));
        const recoverMs = Math.max(0, parseCssNumber(currentStyle, '--brand-neon-voltage-recover-ms', 0));
        const soundEnabled = parseCssFlag(currentStyle, '--brand-neon-sound-enabled', true);
        const soundVolume = Math.max(0, Math.min(1, parseCssNumber(currentStyle, '--brand-neon-sound-volume', 0.28)));

        setNeonLogoState((previous) => ({
          phase: 'surge',
          sequence: {},
          token: previous.token + 1,
        }));

        neonPhaseTimerRef.current = window.setTimeout(() => {
          if (disposed) return;
          const faultStyle = neonLogoRef.current ? window.getComputedStyle(neonLogoRef.current) : currentStyle;
          const sequence = buildNeonTaxFaultSequence(faultStyle);
          const longestFault = Object.values(sequence).reduce(
            (max, entry) => Math.max(max, Number(entry.delay || 0) + Number(entry.duration || 0)),
            parseCssNumber(faultStyle, '--brand-neon-fault-duration-ms', 420)
          );

          setNeonLogoState((previous) => ({
            phase: 'fault',
            sequence,
            token: previous.token + 1,
          }));

          if (soundEnabled && soundVolume > 0) {
            playNeonLogoFault({ volume: soundVolume, letters: Object.keys(sequence).length });
          }

          neonPhaseTimerRef.current = window.setTimeout(() => {
            if (disposed) return;
            if (recoverMs > 0) {
              setNeonLogoState((previous) => ({ ...previous, phase: 'recover', sequence: {} }));
              neonPhaseTimerRef.current = window.setTimeout(() => {
                if (disposed) return;
                setNeonLogoState((previous) => ({ ...previous, phase: 'idle', sequence: {} }));
                scheduleNextFault();
              }, recoverMs);
              return;
            }

            setNeonLogoState((previous) => ({ ...previous, phase: 'idle', sequence: {} }));
            scheduleNextFault();
          }, Math.max(80, longestFault + 50));
        }, riseMs + holdMs);
      }, Math.max(1000, delay));
    };

    scheduleNextFault();

    return () => {
      disposed = true;
      clearNeonTimers();
    };
  }, []);

  const multiplierRailStyle = useMemo(() => ({
    '--startax-multiplier-rail-scale-base': MULTIPLIER_RAIL_UI.scaleBase,
    '--startax-multiplier-rail-fine': MULTIPLIER_RAIL_UI.fine,
    '--startax-multiplier-rail-effective-scale': `calc(${MULTIPLIER_RAIL_UI.scaleBase} * ${MULTIPLIER_RAIL_UI.fine})`,
    '--startax-multiplier-rail-right': MULTIPLIER_RAIL_UI.right,
    '--startax-multiplier-rail-x': MULTIPLIER_RAIL_UI.x,
    '--startax-multiplier-rail-y': MULTIPLIER_RAIL_UI.y,
    '--startax-multiplier-rail-width': MULTIPLIER_RAIL_UI.width,
    '--startax-multiplier-rail-gap': MULTIPLIER_RAIL_UI.gap,
    '--startax-multiplier-rail-padding-y': MULTIPLIER_RAIL_UI.paddingY,
    '--startax-multiplier-rail-padding-x': MULTIPLIER_RAIL_UI.paddingX,
    '--startax-multiplier-button-size': MULTIPLIER_RAIL_UI.buttonSize,
    '--startax-multiplier-button-font-size': MULTIPLIER_RAIL_UI.buttonFontSize,
  }), []);

  const geckoButtonStyle = useMemo(() => ({
    '--startax-gecko-button-scale': GECKO_BUTTON_UI.scale,
    '--startax-gecko-button-x': GECKO_BUTTON_UI.x,
    '--startax-gecko-button-y': GECKO_BUTTON_UI.y,
  }), []);

  useEffect(() => {
    latestDataRef.current = { transactionCounter, currentReceivers, walletAddress };
  }, [transactionCounter, currentReceivers, walletAddress]);

  useEffect(() => {
    slotStateRef.current = slotState;
  }, [slotState]);
  useEffect(() => {
    autoModeRef.current = autoMode;
    if (!autoMode && autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  }, [autoMode]);


  useEffect(() => {
    if (isPendingTx) return;

    const isManualWalletConnectReveal = Boolean(
      walletAddress &&
      walletConnectNonce &&
      walletConnectNonce !== previousWalletConnectNonceRef.current
    );

    setSlotState((previousState) =>
      buildSlotState({
        transactionCounter,
        currentReceivers,
        walletAddress,
        previousState: isManualWalletConnectReveal ? null : previousState,
        revealOnInitialActive: isManualWalletConnectReveal,
        initialRevealReason: isManualWalletConnectReveal ? 'manual-wallet-connect' : null,
      })
    );

    previousWalletConnectNonceRef.current = walletConnectNonce;
  }, [transactionCounter, currentReceivers, walletAddress, walletConnectNonce, isPendingTx]);

  useEffect(() => {
    if (walletAddress) {
      setShowWalletNotice(false);
      setLeverBroken(false);
      setLeverEnergy(previous => ({ ...previous, power: 0, active: false, pulling: false, spinning: false }));
    } else setShowDisconnectNotice(false);
  }, [walletAddress]);

  useEffect(() => {
    if (isCorrectNetwork) setShowNetworkNotice(false);
  }, [isCorrectNetwork]);

  useEffect(() => {
    if (walletError) setStatus('Wallet error');
  }, [walletError]);


  useEffect(() => {
    const popupOpen = Boolean(
      (showWalletNotice && !walletAddress) ||
      (showNetworkNotice && walletAddress && !isCorrectNetwork) ||
      (showDisconnectNotice && walletAddress) ||
      balanceNotice ||
      claimFailureNotice
    );

    if (popupOpen && !popupOpenStateRef.current) {
      unlockAudio();
      playPopupOpenSynth();
    }

    popupOpenStateRef.current = popupOpen;
  }, [showWalletNotice, showNetworkNotice, showDisconnectNotice, balanceNotice, claimFailureNotice, walletAddress, isCorrectNetwork]);

  const triggerExternalRewardEffect = useCallback((rewardAmount) => {
    const cleanRewardAmount = Math.max(0, Number(rewardAmount));
    if (cleanRewardAmount <= 0) return;

    setLastRewardAmount(cleanRewardAmount);
    playPrize();
    setPrizePulseActive(true);
    setStatus('Prize received');

    if (prizePulseTimerRef.current) clearTimeout(prizePulseTimerRef.current);
    prizePulseTimerRef.current = setTimeout(() => {
      setPrizePulseActive(false);
      prizePulseTimerRef.current = null;
    }, 2800);
  }, []);


  const seenRewardRef = useRef(null);
  useEffect(() => {
    setLastRewardAmount(0);
    setPrizePulseActive(false);
  }, [walletAddress]);
  useEffect(() => {
    if (!confirmedReward || !sameAddress(confirmedReward.wallet, walletAddress) || seenRewardRef.current === confirmedReward.id) return;
    seenRewardRef.current = confirmedReward.id;
    triggerExternalRewardEffect(confirmedReward.amount);
  }, [confirmedReward, walletAddress, triggerExternalRewardEffect]);

  useEffect(() => {
    const numericCounter = Number(transactionCounter || 0);
    const previousCounter = previousCounterRef.current;

    if (previousCounter == null) {
      previousCounterRef.current = numericCounter;
      return undefined;
    }

    const increased = numericCounter > previousCounter;
    previousCounterRef.current = numericCounter;

    if (!increased || !walletAddress) return undefined;

    playTxCounterTick();
    setTxPulseActive(true);
    if (txPulseTimerRef.current) clearTimeout(txPulseTimerRef.current);
    txPulseTimerRef.current = setTimeout(() => {
      setTxPulseActive(false);
      txPulseTimerRef.current = null;
    }, 950);

    return undefined;
  }, [transactionCounter, walletAddress]);

  useEffect(() => () => {
    if (prizePulseTimerRef.current) clearTimeout(prizePulseTimerRef.current);
    if (txPulseTimerRef.current) clearTimeout(txPulseTimerRef.current);
    if (jackpotCelebrationTimerRef.current) clearTimeout(jackpotCelebrationTimerRef.current);
    if (jackpotSimulationTimerRef.current) clearTimeout(jackpotSimulationTimerRef.current);
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
  }, []);

  useEffect(() => {
    if (jackpotSimulationTimerRef.current) {
      clearTimeout(jackpotSimulationTimerRef.current);
      jackpotSimulationTimerRef.current = null;
    }

    setSimulatedJackpot(null);

    if (!walletAddress || LEVER_SWAP_CONFIG.SIMULATE_VANGUARD_JACKPOT_DROP !== true) {
      return undefined;
    }

    const amount = floorTokenAmount(LEVER_SWAP_CONFIG.SIMULATED_VANGUARD_JACKPOT_AMOUNT || 0);
    if (amount <= 0) return undefined;

    const delay = Math.max(0, Number(LEVER_SWAP_CONFIG.SIMULATED_VANGUARD_JACKPOT_DELAY_MS || 0));
    jackpotSimulationTimerRef.current = setTimeout(() => {
      setSimulatedJackpot({
        receiver: walletAddress,
        amount,
        key: `sim-${String(walletAddress || '').toLowerCase()}-${Date.now()}-${amount}`,
      });
      jackpotSimulationTimerRef.current = null;
    }, delay);

    return () => {
      if (jackpotSimulationTimerRef.current) {
        clearTimeout(jackpotSimulationTimerRef.current);
        jackpotSimulationTimerRef.current = null;
      }
    };
  }, [walletAddress]);

  const tokenBalanceDisplay = useMemo(() => floorTokenAmount(tokenBalance), [tokenBalance]);
  const lastSwapAmountSource = lastSuccessfulSwap?.amount ?? lastSwapAmount;
  const lastSwapAmountDisplay = useMemo(() => floorTokenAmount(lastSwapAmountSource), [lastSwapAmountSource]);
  const lastSwapTxHash = lastSuccessfulSwap?.txHash || null;
  const lastSwapSide = lastSuccessfulSwap?.mode || null;
  const jackpotTotalDisplay = useMemo(() => floorTokenAmount(jackpotTotal), [jackpotTotal]);
  const jackpotClaimDisplay = useMemo(() => floorTokenAmount(jackpotFibPot), [jackpotFibPot]);
  const hasUnclaimedJackpot = Boolean(Number(jackpotFibPot) > 0 && /^0x[0-9a-f]{40}$/i.test(jackpotFiboReceiver) && !/^0x0{40}$/i.test(jackpotFiboReceiver));
  const displayedLastJackpot = hasUnclaimedJackpot
    ? { address: jackpotFiboReceiver, amount: String(jackpotClaimDisplay) }
    : lastJackpot.claim;
  const realJackpotClaimable = Boolean(walletAddress && jackpotClaimDisplay > 0 && sameAddress(walletAddress, jackpotFiboReceiver));
  const simulatedJackpotClaimable = Boolean(
    simulatedJackpot &&
    walletAddress &&
    floorTokenAmount(simulatedJackpot.amount) > 0 &&
    sameAddress(walletAddress, simulatedJackpot.receiver)
  );
  const simulatedJackpotActive = simulatedJackpotClaimable && !realJackpotClaimable;
  const jackpotClaimable = realJackpotClaimable || simulatedJackpotClaimable;
  const effectiveJackpotClaimDisplay = simulatedJackpotActive ? floorTokenAmount(simulatedJackpot.amount) : jackpotClaimDisplay;
  const effectiveJackpotTotalDisplay = simulatedJackpotActive
    ? Math.max(jackpotTotalDisplay, effectiveJackpotClaimDisplay)
    : jackpotTotalDisplay;
  useEffect(() => {
    if (!jackpotClaimable) {
      setJackpotCelebrating(false);
      return undefined;
    }

    const celebrationKey = simulatedJackpotActive
      ? simulatedJackpot?.key
      : `${String(jackpotFiboReceiver || '').toLowerCase()}-${jackpotClaimDisplay}-${Number(jackpotLastFiboNumber || 0)}`;
    if (lastCelebratedJackpotRef.current === celebrationKey) return undefined;
    lastCelebratedJackpotRef.current = celebrationKey;

    unlockAudio();
    playJackpotAward();
    setJackpotCelebrating(true);
    setStatus('Jackpot claim ready');

    if (jackpotCelebrationTimerRef.current) clearTimeout(jackpotCelebrationTimerRef.current);
    jackpotCelebrationTimerRef.current = setTimeout(() => {
      setJackpotCelebrating(false);
      jackpotCelebrationTimerRef.current = null;
    }, 5200);

    return undefined;
  }, [jackpotClaimable, jackpotFiboReceiver, jackpotClaimDisplay, jackpotLastFiboNumber, simulatedJackpotActive, simulatedJackpot]);

  const cancelAutoLoop = useCallback((nextStatus = null) => {
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    autoModeRef.current = false;
    setAutoMode(false);
    if (nextStatus) setStatus(nextStatus);
  }, []);

  const handleAutoModeChange = useCallback((nextAutoMode) => {
    unlockAudio();
    playPopupButtonSynth();
    const enabled = Boolean(nextAutoMode);
    autoModeRef.current = enabled;
    setAutoMode(enabled);
    if (!enabled && autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    setStatus(enabled ? 'AUTO armed' : 'AUTO off');
  }, []);

  const scheduleAutoLever = useCallback(() => {
    if (!autoModeRef.current) return;
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    autoTimerRef.current = setTimeout(() => {
      autoTimerRef.current = null;
      if (!autoModeRef.current || isPendingTx) return;
      handleLeverRef.current?.({ auto: true });
    }, AUTO_LEVER_DELAY_MS);
  }, [isPendingTx]);

  const handleModeChange = useCallback((nextMode) => {
    const normalizedMode = nextMode === 'SELL' ? 'SELL' : 'BUY';
    unlockAudio();
    if (normalizedMode === 'SELL') playSellButtonSynth();
    else playBuyButtonSynth();
    setMode(normalizedMode);
  }, []);

  const handleSlotNavClick = useCallback((item, index) => {
    unlockAudio();
    playSideButtonSynth(index);
    openSlotNavLink(item.url);
  }, []);

  const handleGeckoTerminalClick = useCallback(() => {
    unlockAudio();
    playGeckoLaunchSynth();
    openGeckoTerminalPool();
  }, []);

  const handleManifestoClick = useCallback(() => {
    unlockAudio();
    playSideButtonSynth(5);
    openStartaxManifesto();
  }, []);

  const handleSushiSwapClick = useCallback(() => {
    unlockAudio();
    playBuyButtonSynth();
    openSushiSwap();
  }, []);

  const handleClaimJackpot = async () => {
    if (!jackpotClaimable || jackpotClaimPending || isPendingTx) return;
    unlockAudio();
    setClaimFailureNotice(null);
    setJackpotClaimPending(true);
    setStatus(simulatedJackpotActive ? 'Claim simulation' : 'Claim jackpot');

    try {
      if (typeof onClaimJackpot === 'function') {
        await onClaimJackpot({ silent: true });
      } else {
        throw new Error('Jackpot claim handler unavailable.');
      }
      playJackpotClaim();
      setSimulatedJackpot(null);
      setJackpotCelebrating(false);
      setStatus('Jackpot claimed');
    } catch (error) {
      console.error(error);
      playError();
      const rejected = error?.code === 4001 || error?.code === 'ACTION_REJECTED' || error?.code === 'USER_REJECTED';
      if (rejected) {
        setStatus('Claim rejected');
        setClaimFailureNotice({
          title: 'Claim rejected',
          text: 'The wallet rejected the Jackpot claim request.',
        });
      } else {
        setStatus('Claim failed');
        setClaimFailureNotice({
          title: 'Claim failed',
          text: simulatedJackpotActive
            ? 'Winning address does not match the connected wallet. This was a UI simulation; the smart contract rejected the claim.'
            : 'Winning address does not match the connected wallet, or the Jackpot is no longer claimable.',
        });
      }
    } finally {
      setJackpotClaimPending(false);
    }
  };

  const handleConnect = async () => {
    if (isPendingTx) return;
    unlockAudio();

    try {
      let connectedAccount = null;

      if (typeof onConnectWallet === 'function') {
        connectedAccount = await onConnectWallet();
      }

      if (!connectedAccount && window.ethereum) {
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        connectedAccount = accounts?.[0] || null;
      }

      if (connectedAccount || walletAddress) {
        setLeverBroken(false);
        resetLeverEnergyVisual();
        setShowWalletNotice(false);
        setShowNetworkNotice(false);
        setBalanceNotice(null);
        setStatus(isCorrectNetwork ? 'Wallet connected' : 'Switch network');
      } else {
        setStatus('Connect wallet');
      }
    } catch (error) {
      console.error(error);
      playError();
      setStatus('Wallet rejected');
    }
  };

  const handleDisconnectWallet = async ({ revokeAllowances = false } = {}) => {
    if (isPendingTx || disconnectActionPending) return;
    unlockAudio();

    const actionLabel = revokeAllowances ? 'revoke' : 'disconnect';
    setDisconnectActionPending(actionLabel);
    setStatus(revokeAllowances ? 'Revoking permissions' : 'Disconnecting wallet');

    try {
      let revokeSummary = null;
      if (typeof onDisconnectWallet === 'function') {
        const result = await onDisconnectWallet({
          revokeAllowances,
          onRevokeStep: (stepPayload) => {
            const step = stepPayload?.step;
            if (step === 'checking_allowance') setStatus('Checking allowances');
            if (step === 'revoke_requested') setStatus('Confirm revoke');
            if (step === 'revoke_submitted') setStatus('Revoking allowance');
            if (step === 'revoke_confirmed') setStatus('Allowance revoked');
          },
        });
        revokeSummary = result?.revokeSummary || null;
      }
      setShowDisconnectNotice(false);
      setShowWalletNotice(false);
      setShowNetworkNotice(false);
      setBalanceNotice(null);
      setStatus(
        revokeAllowances
          ? (revokeSummary?.revokedCount > 0 ? 'Permissions revoked' : 'No active allowances')
          : 'Wallet disconnected'
      );
    } catch (error) {
      console.error(error);
      playError();
      const code = error?.code;
      if (code === 4001 || code === 'ACTION_REJECTED' || code === 'USER_REJECTED') {
        setStatus('Revoke rejected');
      } else if (code === 'WRONG_NETWORK') {
        setStatus('Switch to Optimism');
      } else {
        setStatus(revokeAllowances ? 'Revoke failed' : 'Disconnect failed');
      }
    } finally {
      setDisconnectActionPending(null);
    }
  };

  const handleFooterExplorerClick = useCallback((kind, target) => {
    if (!target) return;
    unlockAudio();
    playFooterButtonSynth(kind);
    if (kind === 'address') openOptimismAddress(target);
    else openOptimismTx(target);
  }, []);

  const handlePopupAction = useCallback((action) => {
    unlockAudio();
    playPopupButtonSynth();
    if (typeof action === 'function') action();
  }, []);

  const handleWalletHudClick = () => {
    if (isPendingTx) return;
    unlockAudio();
    playFooterButtonSynth('wallet');

    if (walletAddress) {
      setShowDisconnectNotice(true);
      setShowWalletNotice(false);
      setShowNetworkNotice(false);
      setBalanceNotice(null);
      setStatus('Wallet connected');
      return;
    }

    handleConnect();
  };

  const handleWalletButtonPointerDown = (event) => {
    event.stopPropagation();
  };

  const handleSwapMultiplierSelect = (nextMultiplier) => {
    if (isPendingTx || autoModeRef.current) {
      if (autoModeRef.current) {
        unlockAudio();
        playError();
        setStatus('AUTO must be off');
      }
      return;
    }
    unlockAudio();
    const numericMultiplier = Number(nextMultiplier) || 1;
    setSwapMultiplier(numericMultiplier);
    playMultiplierSelect(numericMultiplier);
    setStatus(`Swap power ${numericMultiplier}X`);
  };

  const handleLever = async ({ auto = false } = {}) => {
    if (isPendingTx) return;

    if (!walletAddress) {
      unlockAudio();
      setLeverBroken(true);
      playBrokenLever();
      playLeverFusePop();
      setShowWalletNotice(true);
      setStatus('Connect wallet');
      return;
    }

    if (!isCorrectNetwork) {
      unlockAudio();
      playConnectPrompt();
      setShowNetworkNotice(true);
      setStatus('Switch to Optimism');
      return;
    }

    setBalanceNotice(null);
    setLeverBroken(false);


    unlockAudio();
    setStatus(LEVER_SWAP_CONFIG.WAIT_FOR_PRECHECK_OK_BEFORE_SPIN ? `${auto ? 'AUTO ' : ''}${mode} ${swapMultiplier}X checking` : `${auto ? 'AUTO ' : ''}${mode} ${swapMultiplier}X pending`);

    try {
      let swapDryRun = null;
      let visualSpinStarted = false;
      let swapConfirmed = false;

      const startVisualSpin = () => {
        if (visualSpinStarted) return;
        visualSpinStarted = true;
        playLever();
        playSpinLoopHint();
        setIsPendingTx(true);
      };

      if (!LEVER_SWAP_CONFIG.WAIT_FOR_PRECHECK_OK_BEFORE_SPIN) {
        startVisualSpin();
      }

      const handleSwapStep = ({ step }) => {
        if (step === 'precheck_passed') {
          if (LEVER_SWAP_CONFIG.WAIT_FOR_PRECHECK_OK_BEFORE_SPIN) startVisualSpin();
          setStatus(`${mode} ${swapMultiplier}X pending`);
        }
        else if (step === 'dry_run_ready') setStatus('Dry-run ready');
        else if (step === 'approval_requested') setStatus('Confirm approval');
        else if (step === 'approval_confirmed') setStatus('Approval confirmed');
        else if (step === 'swap_requested') {

          setStatus('Confirm swap');
        }
        else if (step === 'swap_submitted') {

          setStatus('TX pending');
        }
        else if (step === 'swap_confirmed') {
          swapConfirmed = true;

          setStatus('TX confirmed');
        }
        else if (step === 'swap_reverted') setStatus('TX reverted');
        else if (step === 'user_rejected_signature') setStatus('Wallet rejected');
        else if (step === 'failed') setStatus('TX failed');
      };

      if (typeof onLeverSwap === 'function') {
        setStatus(LEVER_SWAP_CONFIG.WAIT_FOR_PRECHECK_OK_BEFORE_SPIN ? `${auto ? 'AUTO ' : ''}${mode} ${swapMultiplier}X building` : `${auto ? 'AUTO ' : ''}${mode} ${swapMultiplier}X spinning`);
        swapDryRun = await onLeverSwap({ mode, swapMultiplier, onStep: handleSwapStep });
      } else {
        startVisualSpin();
        await sleep(2600);
        swapConfirmed = true;
      }

      await sleep(900);

      const latest = latestDataRef.current;
      const previousVisualState = slotStateRef.current;
      const refreshedState = buildSlotState({
        transactionCounter: latest.transactionCounter,
        currentReceivers: latest.currentReceivers,
        walletAddress: latest.walletAddress,
        previousState: previousVisualState,
      });

      const hadPrizeBefore = previousVisualState.lines.some((line) => line.activeForWallet);
      const hasPrizeAfter = refreshedState.lines.some((line) => line.activeForWallet);
      const newPrize = refreshedState.lines.some(
        (line, index) => Boolean(line.revealToStar || (line.activeForWallet && !previousVisualState.lines[index]?.activeForWallet))
      );

      setSlotState(settleSpinResultLines(refreshedState, { preserveActiveReveals: newPrize || hasPrizeAfter }));
      setStatus(swapDryRun?.realExecution?.enabled ? (newPrize ? 'Confirmed · prize' : 'Confirmed · no prize') : 'Dry-run ready');

      if (newPrize || (!hadPrizeBefore && hasPrizeAfter)) playPrize();
      else playTxConfirmedNeutral();

      if (autoModeRef.current && (swapConfirmed || swapDryRun?.realExecution?.enabled === false)) {
        setStatus(`AUTO next in ${(AUTO_LEVER_DELAY_MS / 1000).toFixed(1)}s`);
        scheduleAutoLever();
      }
    } catch (error) {
      console.error(error);
      playError();
      cancelAutoLoop('AUTO stopped');
      if (error?.code === 'INSUFFICIENT_OPTIMISM_ETH_BALANCE') {
        setIsPendingTx(false);
        setLeverBroken(true);
        playBrokenLever();
        playLeverFusePop();
        setStatus('Insufficient ETH');
        setBalanceNotice({
          type: 'eth',
          title: 'Need Optimism ETH',
          text: 'You need ETH on Optimism to launch a BUY swap and pay network gas.',
        });
      } else if (error?.code === 'INSUFFICIENT_TAX_BALANCE') {
        setIsPendingTx(false);
        setLeverBroken(true);
        playBrokenLever();
        playLeverFusePop();
        setStatus('Insufficient ★TAX');
        setBalanceNotice({
          type: 'tax',
          title: 'Not enough ★TAX',
          text: 'Your wallet does not have enough ★TAX to launch this SELL swap.',
        });
      } else if (error?.code === 'INSUFFICIENT_OPTIMISM_ETH_GAS_BALANCE') {
        setIsPendingTx(false);
        setLeverBroken(true);
        playBrokenLever();
        playLeverFusePop();
        setStatus('Need gas ETH');
        setBalanceNotice({
          type: 'eth',
          title: 'Need gas on Optimism',
          text: 'You need a small ETH balance on Optimism to pay gas for approvals and swaps.',
        });
      } else if (error?.code === 'SWAP_REVERTED') {
        setStatus('TX reverted');
      }
      else if (error?.code === 'USER_REJECTED_SIGNATURE' || error?.code === 4001 || error?.code === 'ACTION_REJECTED') {
        setStatus('Wallet rejected');
      }
      else if(error?.code === 'TRANSACTION_PENDING') {
        setStatus('TX confirmation pending');
        setBalanceNotice({title:'Confirmation pending',text:'The transaction was sent. Check Activity or your wallet; do not repeat it while confirmation is pending.'});
      }
      else setStatus('TX failed');
    } finally {
      setIsPendingTx(false);
    }
  };

  handleLeverRef.current = handleLever;

  const neonLogoSubtitle = rewardsWinnersView ? NEON_LOGO_SUBTITLE_WINNERS : NEON_LOGO_SUBTITLE_DEFAULT;

  const neonPhaseClass = neonLogoState.phase === 'surge'
    ? 'isSurging'
    : neonLogoState.phase === 'fault'
      ? 'isFaulting'
      : neonLogoState.phase === 'recover'
        ? 'isRecovering'
        : 'isIdle';

  const renderNeonTaxLetter = (letter, index) => {
    const fault = neonLogoState.sequence?.[index];
    return (
      <span
        key={`tax-${letter}-${index}`}
        className={`slotBrandNeonLetter slotBrandNeonLetter--tax ${fault ? 'isLetterFaulting' : ''}`}
        style={fault ? {
          '--neon-letter-fault-delay': `${fault.delay}ms`,
          '--neon-letter-fault-duration': `${fault.duration}ms`,
        } : undefined}
        aria-hidden="true"
      >
        {letter}
      </span>
    );
  };

  return (
    <div className="slotViewportStage">
      <div className="slotViewportScene">
        <main className={`slotAppShell ${jackpotCelebrating ? 'isJackpotCelebration' : ''} ${rewardsWinnersView ? 'isRewardsWinnersView' : ''}`}>

      <button
        type="button"
        className="buy-button slotOriginalSushiButton"
        onClick={handleSushiSwapClick}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label="Open StarTax swap on Sushi"
        title="Open StarTax swap on Sushi"
      >
        <span className="buy-text">SUSHI</span>
        <span className="hover-text">THE FIRST<br />OF ITS<br />KIND</span>
      </button>

      <div className="slotTransactionMinimum" aria-label="Minimum ★TAX to spin">
        <strong>{transactionMinimum == null ? "…" : format(transactionMinimum)}</strong>
        <span>★TAX TO SPIN</span>
      </div>

      <img
        className="slotBrandLogo slotBrandLogo--static"
        src={logoUrl}
        alt={`StarTax · ${neonLogoSubtitle}`}
        role="button"
        tabIndex={0}
        onClick={handleLogoRewardsToggle}
        onKeyDown={handleLogoRewardsToggleKeyDown}
        onPointerDown={(event) => event.stopPropagation()}
        title={rewardsWinnersView ? 'Show slot reels' : 'Show active reward winners'}
      />

      <div
        ref={neonLogoRef}
        className={`slotBrandNeon ${neonPhaseClass} ${rewardsWinnersView ? 'isRewardsWinnersView' : ''}`}
        role="button"
        tabIndex={0}
        aria-pressed={rewardsWinnersView}
        aria-label={`StarTax · ${neonLogoSubtitle}`}
        data-neon-phase={neonLogoState.phase}
        data-rewards-winners-view={rewardsWinnersView ? '1' : '0'}
        onClick={handleLogoRewardsToggle}
        onKeyDown={handleLogoRewardsToggleKeyDown}
        onPointerDown={(event) => event.stopPropagation()}
        title={rewardsWinnersView ? 'Show slot reels' : 'Show active reward winners'}
      >
        <div className="slotBrandNeonMain" aria-hidden="true">
          <span className="slotBrandNeonLetter slotBrandNeonLetter--star">S</span>
          <span className="slotBrandNeonLetter slotBrandNeonLetter--star">t</span>
          <span className="slotBrandNeonLetter slotBrandNeonLetter--star">a</span>
          <span className="slotBrandNeonLetter slotBrandNeonLetter--star">r</span>
          {NEON_TAX_LETTERS.map(renderNeonTaxLetter)}
        </div>
        <div className="slotBrandNeonSubtitle" aria-hidden="true">{neonLogoSubtitle}</div>
      </div>

      <div className={`slotTxCounterHero ${txPulseActive ? 'isTxPulse' : ''}`} aria-label="Transaction counter">
        <strong>{format(transactionCounter)}</strong>
      </div>

      <button
        type="button"
        className={`slotJackpotHeader ${jackpotClaimable ? 'isClaimable' : ''} ${simulatedJackpotActive ? 'isSimulated' : ''} ${jackpotCelebrating ? 'isJackpotCelebration' : ''}`}
        onClick={jackpotClaimable ? handleClaimJackpot : () => openSlotNavLink('https://optimistic.etherscan.io/address/0x9c070b1a5b60e4120c9c0ea0cd246456e701e071')}
        disabled={jackpotClaimable && (jackpotClaimPending || isPendingTx)}
        aria-label={jackpotClaimable ? `Claim ${effectiveJackpotClaimDisplay} StarTax jackpot` : `${effectiveJackpotTotalDisplay} StarTax in jackpot`}
        title={jackpotClaimable ? 'Claim Jackpot' : 'View jackpot on Etherscan (new tab)'}
      >
        <span className="slotJackpotLabel">{jackpotClaimable ? 'CLAIM' : 'JACKPOT'}</span>
        <strong>{format(jackpotClaimable ? effectiveJackpotClaimDisplay : effectiveJackpotTotalDisplay)}</strong>
        <span className="slotJackpotUnit">★TAX</span>
        {jackpotClaimPending && <span className="slotJackpotPending">CONFIRM</span>}
        {simulatedJackpotActive && !jackpotClaimPending && <span className="slotJackpotPending slotJackpotPending--sim">SIM</span>}
      </button>

      {showWalletNotice && !walletAddress && (
        <div className="slotWalletNoticeBackdrop" role="dialog" aria-modal="true" aria-label="Connect your wallet">
          <div className="slotWalletNotice">
            <div className="slotWalletNoticeGlow" />
            <div className="slotWalletNoticeTitle">Connect your wallet</div>
            <div className="slotWalletNoticeText">
              The lever needs your browser wallet before it can launch a swap.
            </div>
            <button type="button" className="slotWalletNoticeButton" onClick={() => handlePopupAction(handleConnect)}>
              Connect wallet
            </button>
          </div>
        </div>
      )}

      {showNetworkNotice && walletAddress && !isCorrectNetwork && (
        <div className="slotWalletNoticeBackdrop" role="dialog" aria-modal="true" aria-label="Switch to Optimism">
          <div className="slotWalletNotice">
            <div className="slotWalletNoticeGlow" />
            <div className="slotWalletNoticeTitle">Switch to Optimism</div>
            <div className="slotWalletNoticeText">
              The lever is connected to the Optimism game contract. Switch your browser wallet to Optimism to continue.
            </div>
            <button type="button" className="slotWalletNoticeButton" onClick={() => handlePopupAction(handleConnect)}>
              Switch network
            </button>
          </div>
        </div>
      )}

      {showDisconnectNotice && walletAddress && (
        <div className="slotWalletNoticeBackdrop" role="dialog" aria-modal="true" aria-label="Disconnect wallet">
          <div className="slotWalletNotice slotDisconnectNotice">
            <div className="slotWalletNoticeGlow" />
            <div className="slotWalletNoticeTitle">Disconnect wallet?</div>
            <div className="slotWalletNoticeText">
              Leave the machine, or revoke StarTax swap allowances first. Revoking permissions requires Optimism gas and wallet confirmations.
            </div>
            <div className="slotWalletNoticeActions slotWalletNoticeActions--disconnect">
              <button
                type="button"
                className="slotWalletNoticeButton slotWalletNoticeButton--stay"
                onClick={() => handlePopupAction(() => setShowDisconnectNotice(false))}
                disabled={Boolean(disconnectActionPending)}
              >
                Stay connected
              </button>
              <button
                type="button"
                className="slotWalletNoticeButton slotWalletNoticeButton--danger"
                onClick={() => handlePopupAction(() => handleDisconnectWallet({ revokeAllowances: false }))}
                disabled={Boolean(disconnectActionPending)}
              >
                {disconnectActionPending === 'disconnect' ? 'Disconnecting…' : 'Disconnect only'}
              </button>
              <button
                type="button"
                className="slotWalletNoticeButton slotWalletNoticeButton--revoke"
                onClick={() => handlePopupAction(() => handleDisconnectWallet({ revokeAllowances: true }))}
                disabled={Boolean(disconnectActionPending)}
              >
                {disconnectActionPending === 'revoke' ? 'Revoking…' : 'Revoke & disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {balanceNotice && (
        <div className="slotWalletNoticeBackdrop" role="dialog" aria-modal="true" aria-label={balanceNotice.title}>
          <div className={`slotWalletNotice slotBalanceNotice slotBalanceNotice--${balanceNotice.type}`}>
            <div className="slotWalletNoticeGlow" />
            <div className="slotWalletNoticeTitle">{balanceNotice.title}</div>
            <div className="slotWalletNoticeText">
              {balanceNotice.text}
            </div>
            <button type="button" className="slotWalletNoticeButton" onClick={() => handlePopupAction(() => { setBalanceNotice(null); setLeverBroken(false); resetLeverEnergyVisual(); })}>
              OK
            </button>
          </div>
        </div>
      )}

      {claimFailureNotice && (
        <div className="slotWalletNoticeBackdrop" role="dialog" aria-modal="true" aria-label={claimFailureNotice.title}>
          <div className="slotWalletNotice slotClaimFailureNotice">
            <div className="slotWalletNoticeGlow" />
            <div className="slotWalletNoticeTitle">{claimFailureNotice.title}</div>
            <div className="slotWalletNoticeText">
              {claimFailureNotice.text}
            </div>
            <button type="button" className="slotWalletNoticeButton slotWalletNoticeButton--danger" onClick={() => handlePopupAction(() => setClaimFailureNotice(null))}>
              OK
            </button>
          </div>
        </div>
      )}

      <section className={`slotMachineWrap ${prizePulseActive ? 'isPrizePulse' : ''} ${txPulseActive ? 'isTxPulse' : ''} ${jackpotCelebrating ? 'isJackpotCelebration' : ''}`}>
        {rewardsWinnersView && <div className={`slotLastJackpotClaim ${hasUnclaimedJackpot ? 'isUnclaimed' : ''}`} aria-label="Last jackpot winner">
          <span className="slotLastJackpotTitle">LAST JACKPOT WINNER</span>
          {displayedLastJackpot ? (
            <div className="slotLastJackpotDetails">
              <a href={`https://optimistic.etherscan.io/address/${displayedLastJackpot.address}#tokentxns`} target="_blank" rel="noopener noreferrer" title={`Open ${displayedLastJackpot.address} on Optimism Explorer`}>
                {displayedLastJackpot.address}
              </a>
              <strong>{displayedLastJackpot.amount.split('.')[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')} ★TAX</strong>
              {hasUnclaimedJackpot && <span className="slotLastJackpotUnclaimed">UNCLAIMED</span>}
              {!hasUnclaimedJackpot && lastJackpot.status === 'unavailable' && <span>UPDATE UNAVAILABLE</span>}
            </div>
          ) : <span>{lastJackpot.status === 'loading' ? 'LOADING…' : lastJackpot.status === 'empty' ? 'NO CLAIMS YET' : 'HISTORY UNAVAILABLE'}</span>}
        </div>}
        <SlotMachine
          state={slotState}
          transactionCounter={transactionCounter}
          spinning={isPendingTx}
          mode={mode}
          autoMode={autoMode}
          onModeChange={handleModeChange}
          onAutoModeChange={handleAutoModeChange}
          onSpin={handleLever}
          onLeverEnergyChange={handleLeverEnergyChange}
          leverBroken={leverBroken}
        />

        <div
          className={`activeRewardsWinnersOverlay ${rewardsWinnersView ? 'isVisible' : ''}`}
          aria-hidden={rewardsWinnersView ? 'false' : 'true'}
          inert={rewardsWinnersView ? undefined : ''}
        >
          <div className="activeRewardsWinnersPanel" role="table" aria-label="Active Rewards Winners">
            {activeRewardsWinnerRows.map((row) => (
              <div
                key={row.key}
                className={`activeRewardsWinnerRow activeRewardsWinnerRow--${row.className} ${row.hasAddress ? 'hasAddress' : 'isEmpty'} ${row.isConnectedWallet ? 'isConnectedWallet' : ''}`}
                role="row"
              >
                <span className={`activeRewardsWinnerName activeRewardsWinnerName--${row.className}`} role="cell">
                  {row.label}
                </span>

                <span className="activeRewardsWinnerAddress" role="cell">
                  {row.hasAddress && rewardsWinnersView ? (
                    <a
                      href={row.explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                      title={`Open ${row.address} on Optimism Explorer`}
                    >
                      {row.address}
                    </a>
                  ) : row.hasAddress ? (
                    <span>{row.address}</span>
                  ) : (
                    <span className="activeRewardsWinnerAddressEmpty">NO ADDRESS</span>
                  )}
                </span>

                <span className="activeRewardsWinnerRemaining" role="cell" aria-label={`${row.label} remaining transactions`}>
                  {row.remaining == null ? '—' : `${format(row.remaining)} TX`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>


      <section className="slotHudBar" aria-label="Estado de la máquina">
        <button className="slotHudButton slotWalletCard" type="button" onPointerDown={handleWalletButtonPointerDown} onClick={handleWalletHudClick} disabled={isPendingTx} title={walletAddress ? 'Disconnect wallet' : 'Connect wallet'}>
          <span>Wallet</span>
          <strong>{shortAddress(walletAddress)}</strong>
        </button>
        <button
          className={`slotHudCard slotHudButton slotTaxBalanceCard ${prizePulseActive ? 'isPrizePulse' : ''} ${walletAddress ? 'isClickable' : ''}`}
          type="button"
          onClick={() => handleFooterExplorerClick('address', walletAddress)}
          disabled={!walletAddress}
          title={walletAddress ? `Open wallet ${walletAddress} on Optimism Explorer` : 'Connect wallet to open address'}
        >
          <span>★TAX Balance</span>
          <strong>{format(tokenBalanceDisplay)}</strong>
        </button>
        <div className={`slotHudCard slotLastRewardCard ${lastRewardAmount > 0 ? 'hasReward' : ''}`}>
          <span>Last Reward</span>
          <strong>{lastRewardAmount > 0 ? `+${format(Math.floor(lastRewardAmount))} ★TAX` : '0'}</strong>
        </div>
        <button
          className={`slotHudCard slotHudButton slotLastSwapCard ${lastSwapSide === 'BUY' ? 'isBuy' : lastSwapSide === 'SELL' ? 'isSell' : ''} ${lastSwapTxHash ? 'isClickable' : ''}`}
          type="button"
          onClick={() => handleFooterExplorerClick('tx', lastSwapTxHash)}
          disabled={!lastSwapTxHash}
          title={lastSwapTxHash ? `Open last transaction ${lastSwapTxHash}` : 'No transaction hash yet'}
        >
          <span>Last Swap Amount</span>
          <strong>{format(lastSwapAmountDisplay)}</strong>
        </button>
        <div className={`slotHudStatus ${!isCorrectNetwork && walletAddress ? 'isWarning' : ''}`} title={walletError || status}>
          {walletAddress && !isCorrectNetwork ? 'Switch to Optimism' : status}
        </div>
      </section>

      <aside className={`slotAccessCluster ${isPendingTx ? 'isSpinning' : ''}`} aria-label="StarTax access and market links">
        <nav className={`slotNavBar ${isPendingTx ? 'isSpinning' : ''}`} aria-label="StarTax navigation links">
          {SLOT_NAV_LINKS.map((item, index) => (
            <button
              key={item.key}
              type="button"
              className={`slotNavButton slotNavButton--${item.key}`}
              data-nav-label={item.label}
              onClick={() => handleSlotNavClick(item, index)}
              onPointerDown={(event) => event.stopPropagation()}
              aria-label={`Open ${item.label}`}
            >
              <span className="slotNavSpinLights" aria-hidden="true" />
              <SlotNavIcon item={item} />
              <span className="slotNavText">
                <span className="slotNavLabel">{item.label}</span>
                <span className="slotNavEyebrow">{item.eyebrow}</span>
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <button
        type="button"
        className={`slotManifestoButton ${isPendingTx ? 'isSpinning' : ''}`}
        onClick={handleManifestoClick}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label="Open the StarTax Manifesto"
        title="Open the StarTax Manifesto"
      >
        <span className="slotManifestoButtonGlow" aria-hidden="true" />
        <i className="slotManifestoSpinLights" aria-hidden="true" />
        <span>MANIFESTO</span>
      </button>

      <DesktopDownload />

      <nav
        className={`slotMultiplierRail slotMultiplierRail--${swapMultiplier} ${isPendingTx ? 'isSpinning' : ''} ${autoMode ? 'isAutoLocked' : ''}`}
        style={multiplierRailStyle}
        aria-label="Swap amount multiplier"
      >
        {SWAP_MULTIPLIER_OPTIONS.map((multiplier) => (
          <button
            key={multiplier}
            type="button"
            className={`slotMultiplierButton slotMultiplierButton--${multiplier} ${swapMultiplier === multiplier ? 'isActive' : ''}`}
            onClick={() => handleSwapMultiplierSelect(multiplier)}
            onPointerDown={(event) => event.stopPropagation()}
            disabled={isPendingTx || autoMode}
            aria-pressed={swapMultiplier === multiplier}
            aria-label={`Set swap multiplier to ${multiplier}X`}
            title={`Swap amount ${multiplier}X`}
          >
            <i className="slotMultiplierSpinLights" aria-hidden="true" />
            <span>{multiplier}X</span>
          </button>
        ))}
      </nav>


      <button
        type="button"
        className={`slotGeckoTerminalButton slotGeckoTerminalButton--railDock ${isPendingTx ? 'isSpinning' : ''}`}
        style={geckoButtonStyle}
        onClick={handleGeckoTerminalClick}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label="Open StarTax pool on GeckoTerminal"
        title="Open StarTax pool on GeckoTerminal"
      >
        <span className="slotGeckoTerminalGlow" aria-hidden="true" />
        <img src={geckoButtonUrl} alt="" aria-hidden="true" />
        <span className="slotGeckoTerminalText">CHART</span>
      </button>

      <div
        className={`slotLeverEnergyMeter ${leverEnergy.active ? 'isActive' : ''} ${leverEnergy.pulling ? 'isPulling' : ''} ${leverEnergy.spinning ? 'isSpinning' : ''} ${leverBroken ? 'isBlown' : ''} ${autoMode ? 'isAuto' : ''} ${leverEnergy.mode === 'SELL' ? 'isSell' : 'isBuy'}`}
        aria-hidden="true"
        style={{ '--lever-energy-power': `${Math.round(leverEnergy.power * 100)}%`, '--lever-energy-level': leverEnergy.power.toFixed(3) }}
      >
        <div className="slotLeverEnergyMeterGlow" />
        <div className="slotLeverEnergyMeterTrack">
          <div className="slotLeverEnergyMeterFill" />
          {Array.from({ length: 18 }).map((_, index) => (
            <span key={index} className="slotLeverEnergyMeterBlock" />
          ))}
        </div>
      </div>

      {LEVER_SWAP_CONFIG.SHOW_GUARDED_EXECUTION_REPORT && lastSwapDebugReport && (
        <section className="slotDebugPanel" aria-label="Guarded execution report">
          <div className="slotDebugTitle">Guarded execution report</div>
          <div className="slotDebugGrid">
            <div><span>Mode</span><strong className={lastSwapDebugReport.mode === 'BUY' ? 'debugBuy' : 'debugSell'}>{lastSwapDebugReport.mode}</strong></div>
            <div><span>Amount</span><strong>{format(lastSwapDebugReport.amount)}</strong></div>
            <div><span>Tx target</span><strong>{lastSwapDebugReport.txTo}</strong></div>
            <div><span>Value ETH</span><strong>{lastSwapDebugReport.value}</strong></div>
            <div><span>Approvals</span><strong>{lastSwapDebugReport.approvalsNeeded}</strong></div>
            <div><span>Validation</span><strong>{lastSwapDebugReport.validationStatus}</strong></div>
            <div><span>Warnings</span><strong>{lastSwapDebugReport.warnings}</strong></div>
            <div><span>Real send</span><strong>{lastSwapDebugReport.dryRunOnly ? 'disabled' : 'enabled'}</strong></div>
          </div>
        </section>
      )}
        </main>
      </div>
    </div>
  );
}
